/* Doodhvale header behaviour (sections/dvf-header.liquid).
   - <dvf-header>: desktop mega menus (hover intent, tap-to-open on touch, keyboard, Escape),
     keeps --header-height in sync with the pinned part of the header, opens the mobile drawer.
   - Mobile drawer: native <dialog> (focus trap, Escape, inert page) with slide animation and scroll lock.
   - [data-dvf-search]: instant suggestions from the predictive search API (debounced, cached, abortable).
   - [data-dvf-cd]: offer countdowns, one shared 1s ticker that pauses while the tab is hidden.
   No dependencies. Works with the theme's <toggle-link> search/cart drawers and <cart-count>. */
(function () {
  'use strict';

  var IST_OFFSET_MS = 330 * 60 * 1000;
  var DESKTOP = '(min-width: 1000px)';
  var OPEN_DELAY = 90;   // hover intent: ignore pointers just passing over the menu
  var CLOSE_DELAY = 220; // grace period for diagonal moves from the link into the panel

  // ------------------------------------------------------------------ countdowns

  // Next time the India wall clock reads hh:mm, as epoch ms (today if still ahead, else tomorrow).
  function nextDailyEnd(hhmm, now) {
    var m = /^(\d{1,2}):(\d{2})$/.exec(String(hhmm || '').trim());
    if (!m) return null;
    var h = Math.min(23, parseInt(m[1], 10));
    var min = Math.min(59, parseInt(m[2], 10));
    var ist = new Date(now + IST_OFFSET_MS);
    var end = Date.UTC(ist.getUTCFullYear(), ist.getUTCMonth(), ist.getUTCDate(), h, min) - IST_OFFSET_MS;
    if (end <= now) end += 864e5;
    return end;
  }

  function pad(n) { return n < 10 ? '0' + n : String(n); }

  // 8h 5m 3s -> "08:05:03"; a day or more -> "1d 09:27:50" (same as snippets/dvf-nav-offer.liquid).
  function formatLeft(ms) {
    var t = Math.max(0, Math.floor(ms / 1000));
    var d = Math.floor(t / 86400);
    var hms = pad(Math.floor(t / 3600) % 24) + ':' + pad(Math.floor(t / 60) % 60) + ':' + pad(t % 60);
    return d > 0 ? d + 'd ' + hms : hms;
  }

  function countdownEnd(el, now) {
    if (el.dataset.daily) return nextDailyEnd(el.dataset.daily, now);
    var end = parseInt(el.dataset.end, 10);
    return isNaN(end) ? null : end;
  }

  var ticker = null;

  function paintCountdowns() {
    var now = Date.now();
    var live = 0;
    document.querySelectorAll('[data-dvf-cd]').forEach(function (el) {
      var end = countdownEnd(el, now);
      if (end === null || end <= now) {
        el.hidden = true; // a fixed-date offer that ran out while the page was open
        return;
      }
      var text = formatLeft(end - now);
      el.querySelectorAll('[data-dvf-cd-t]').forEach(function (t) {
        if (t.textContent !== text) t.textContent = text;
      });
      live++;
    });
    if (!live) stopTicker();
    return live;
  }

  function startTicker() {
    if (ticker || document.hidden) return;
    if (paintCountdowns()) ticker = setInterval(paintCountdowns, 1000);
  }

  function stopTicker() {
    clearInterval(ticker);
    ticker = null;
  }

  document.addEventListener('visibilitychange', function () {
    if (document.hidden) stopTicker(); else startTicker();
  });

  // ------------------------------------------------------------------ search suggestions

  var suggestCache = new Map(); // "url|term" -> html, shared by every search field on the page

  function DvfSearch(form) {
    this.form = form;
    this.input = form.querySelector('input[name="q"]');
    this.results = form.querySelector('[data-dvf-results]');
    this.url = form.dataset.suggestUrl || '/search/suggest';
    this.timer = null;
    this.controller = null;
    this.term = '';
    if (!this.input || !this.results) return;
    this.dismissed = false; // Escape closed the list: keep it closed until the user types again
    this.onInput = this.onInput.bind(this);
    this.onType = this.onType.bind(this);
    this.onFocus = this.onFocus.bind(this);
    this.onKey = this.onKey.bind(this);
    this.onFocusOut = this.onFocusOut.bind(this);
    this.input.addEventListener('input', this.onType);
    this.input.addEventListener('focus', this.onFocus);
    form.addEventListener('keydown', this.onKey);
    form.addEventListener('focusout', this.onFocusOut);
    form.dvfSearch = this;
  }

  DvfSearch.MIN_CHARS = 2;
  DvfSearch.DEBOUNCE = 180;

  DvfSearch.prototype.onType = function () {
    this.dismissed = false;
    this.onInput();
  };

  // Coming back to a field that still holds a term brings its suggestions back (from cache).
  DvfSearch.prototype.onFocus = function () {
    if (!this.dismissed) this.onInput();
  };

  DvfSearch.prototype.onInput = function () {
    var term = this.input.value.trim();
    clearTimeout(this.timer);
    if (term.length < DvfSearch.MIN_CHARS) {
      this.term = term;
      this.abort();
      this.hide();
      return;
    }
    if (term === this.term && !this.results.hidden) return;
    var key = this.url + '|' + term.toLowerCase();
    if (suggestCache.has(key)) {
      this.term = term;
      this.abort();
      this.show(suggestCache.get(key));
      return;
    }
    this.timer = setTimeout(this.fetch.bind(this, term, key), DvfSearch.DEBOUNCE);
  };

  DvfSearch.prototype.fetch = function (term, key) {
    var self = this;
    this.abort();
    this.term = term;
    this.controller = typeof AbortController === 'function' ? new AbortController() : null;
    this.form.setAttribute('aria-busy', 'true');
    var url = this.url + '?q=' + encodeURIComponent(term) +
      '&resources[type]=product,collection,query&resources[limit]=6&resources[options][unavailable_products]=last' +
      '&section_id=dvf-search-suggest';
    return fetch(url, { signal: this.controller && this.controller.signal, credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var box = doc.querySelector('.dvf-sugg');
        if (!box) throw new Error('no suggestions markup');
        suggestCache.set(key, box.outerHTML);
        if (self.term === term && self.input.value.trim() === term) self.show(box.outerHTML);
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        self.hide(); // the form still submits to the search page
      })
      .then(function () {
        self.form.removeAttribute('aria-busy');
      });
  };

  DvfSearch.prototype.abort = function () {
    if (this.controller) this.controller.abort();
    this.controller = null;
  };

  DvfSearch.prototype.show = function (html) {
    this.results.innerHTML = html;
    this.results.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
  };

  DvfSearch.prototype.hide = function () {
    if (this.results.hidden) return;
    this.results.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
  };

  DvfSearch.prototype.options = function () {
    return Array.prototype.slice.call(this.results.querySelectorAll('[data-dvf-opt]'));
  };

  DvfSearch.prototype.onKey = function (e) {
    if (e.key === 'Escape') {
      if (!this.results.hidden) {
        e.stopPropagation(); // keep a surrounding dialog open: first Escape only closes the list
        e.preventDefault();
        this.dismissed = true;
        this.hide();
        this.input.focus();
      }
      return;
    }
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    if (this.results.hidden) return;
    var opts = this.options();
    if (!opts.length) return;
    e.preventDefault();
    var i = opts.indexOf(document.activeElement);
    if (e.key === 'ArrowDown') i = i < opts.length - 1 ? i + 1 : 0;
    else if (i <= 0) { this.input.focus(); return; } else i -= 1;
    opts[i].focus();
  };

  DvfSearch.prototype.onFocusOut = function (e) {
    if (!e.relatedTarget || !this.form.contains(e.relatedTarget)) this.hide();
  };

  DvfSearch.prototype.destroy = function () {
    clearTimeout(this.timer);
    this.abort();
    this.input.removeEventListener('input', this.onType);
    this.input.removeEventListener('focus', this.onFocus);
    this.form.removeEventListener('keydown', this.onKey);
    this.form.removeEventListener('focusout', this.onFocusOut);
    delete this.form.dvfSearch;
  };

  // ------------------------------------------------------------------ <dvf-header>

  var Base = typeof HTMLElement === 'function' ? HTMLElement : function () {};

  class DvfHeader extends Base {
    connectedCallback() {
      this.section = this.closest('.shopify-section') || this.parentElement;
      this.drawer = this.section.querySelector('[data-dvf-drawer]');
      this.items = Array.prototype.slice.call(this.querySelectorAll('[data-dvf-item]'));
      this.openItem = null;
      this.timer = null;
      this.mq = window.matchMedia(DESKTOP);
      this.searches = [];

      this.onPointerOver = this.onPointerOver.bind(this);
      this.onPointerOut = this.onPointerOut.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onFocusOut = this.onFocusOut.bind(this);
      this.onDocClick = this.onDocClick.bind(this);
      this.onMedia = this.onMedia.bind(this);
      this.onDrawerClick = this.onDrawerClick.bind(this);
      this.onDrawerCancel = this.onDrawerCancel.bind(this);
      this.onDrawerClose = this.onDrawerClose.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.onBlockDeselect = this.onBlockDeselect.bind(this);

      this.addEventListener('pointerover', this.onPointerOver);
      this.addEventListener('pointerout', this.onPointerOut);
      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeyDown);
      this.addEventListener('focusout', this.onFocusOut);
      document.addEventListener('click', this.onDocClick);
      if (this.mq.addEventListener) this.mq.addEventListener('change', this.onMedia);
      else if (this.mq.addListener) this.mq.addListener(this.onMedia);

      if (this.drawer) {
        this.drawer.addEventListener('click', this.onDrawerClick);
        this.drawer.addEventListener('cancel', this.onDrawerCancel);
        this.drawer.addEventListener('close', this.onDrawerClose);
      }

      var self = this;
      this.section.querySelectorAll('[data-dvf-search]').forEach(function (form) {
        if (!form.dvfSearch) self.searches.push(new DvfSearch(form));
      });

      this.syncHeight();
      if (window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(function () { self.syncHeight(); });
        this.resizeObserver.observe(this);
      }

      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);

      startTicker();
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      this.removeEventListener('pointerover', this.onPointerOver);
      this.removeEventListener('pointerout', this.onPointerOut);
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKeyDown);
      this.removeEventListener('focusout', this.onFocusOut);
      document.removeEventListener('click', this.onDocClick);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
      if (this.mq.removeEventListener) this.mq.removeEventListener('change', this.onMedia);
      else if (this.mq.removeListener) this.mq.removeListener(this.onMedia);
      if (this.drawer) {
        this.drawer.removeEventListener('click', this.onDrawerClick);
        this.drawer.removeEventListener('cancel', this.onDrawerCancel);
        this.drawer.removeEventListener('close', this.onDrawerClose);
      }
      if (this.resizeObserver) this.resizeObserver.disconnect();
      this.searches.forEach(function (s) { s.destroy(); });
      document.documentElement.classList.remove('dvf-lock');
    }

    // The pinned part = whole header minus the utility bar that scrolls away.
    syncHeight() {
      var util = this.querySelector('.dvf-util');
      var h = this.offsetHeight - (util ? util.offsetHeight : 0);
      document.documentElement.style.setProperty('--header-height', h + 'px');
    }

    // ---- mega menu

    itemOf(node) {
      var item = node && node.closest ? node.closest('[data-dvf-item]') : null;
      return item && this.contains(item) ? item : null;
    }

    panelOf(item) {
      return item.querySelector('[data-dvf-panel]');
    }

    open(item) {
      clearTimeout(this.timer);
      if (this.openItem === item) return;
      if (this.openItem) this.close(this.openItem, true);
      var panel = this.panelOf(item);
      if (!panel) return;
      panel.hidden = false;
      item.classList.add('is-open');
      item.querySelector('[data-dvf-toggle]').setAttribute('aria-expanded', 'true');
      this.openItem = item;
      this.dispatchEvent(new CustomEvent('dvf-nav:open', { bubbles: true, detail: { item: item } }));
    }

    close(item, switching) {
      item = item || this.openItem;
      if (!item) return;
      clearTimeout(this.timer);
      var panel = this.panelOf(item);
      if (panel) panel.hidden = true;
      item.classList.remove('is-open');
      var toggle = item.querySelector('[data-dvf-toggle]');
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
      if (this.openItem === item) this.openItem = null;
      if (!switching) this.dispatchEvent(new CustomEvent('dvf-nav:close', { bubbles: true }));
    }

    onPointerOver(e) {
      if (e.pointerType && e.pointerType !== 'mouse') return; // touch/pen use taps
      if (!this.mq.matches) return;
      var item = this.itemOf(e.target);
      if (!item) return;
      clearTimeout(this.timer);
      if (item === this.openItem) return;
      // Moving between items while one is open switches instantly; a fresh hover waits a beat.
      var self = this;
      this.timer = setTimeout(function () { self.open(item); }, this.openItem ? 0 : OPEN_DELAY);
    }

    onPointerOut(e) {
      if (e.pointerType && e.pointerType !== 'mouse') return;
      var from = this.itemOf(e.target);
      if (!from) return;
      if (this.itemOf(e.relatedTarget) === from) return; // still inside the same item/panel
      clearTimeout(this.timer);
      if (this.openItem === from) {
        var self = this;
        this.timer = setTimeout(function () { self.close(from); }, CLOSE_DELAY);
      }
    }

    onClick(e) {
      var opener = e.target.closest('[data-dvf-drawer-open]');
      if (opener) {
        e.preventDefault();
        this.openDrawer(opener);
        return;
      }
      var toggle = e.target.closest('[data-dvf-toggle]');
      var item = this.itemOf(e.target);
      if (toggle && item) {
        e.preventDefault();
        if (this.openItem === item) this.close(item); else this.open(item);
        return;
      }
      // Touch screens wide enough for the desktop menu: the first tap opens the panel,
      // a second tap on the same link follows it.
      var link = e.target.closest('.dvf-nav__link');
      if (link && item && this.openItem !== item && !this.hoverCapable()) {
        e.preventDefault();
        this.open(item);
      }
    }

    hoverCapable() {
      return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    }

    onKeyDown(e) {
      if (e.key !== 'Escape' || !this.openItem) return;
      var item = this.openItem;
      this.close(item);
      var toggle = item.querySelector('[data-dvf-toggle]');
      if (toggle) toggle.focus();
    }

    onFocusOut(e) {
      if (!this.openItem) return;
      if (e.relatedTarget && this.openItem.contains(e.relatedTarget)) return;
      if (!e.relatedTarget) return; // focus left the window, or a click on nothing focusable; outside clicks are handled below
      this.close();
    }

    onDocClick(e) {
      if (this.openItem && !this.openItem.contains(e.target)) this.close();
    }

    onMedia() {
      if (this.mq.matches) this.closeDrawer(true);
      else if (this.openItem) this.close();
    }

    onBlockSelect(e) {
      if (!this.contains(e.target)) return;
      var item = this.itemOf(e.target);
      if (item && this.mq.matches) this.open(item);
    }

    onBlockDeselect(e) {
      if (this.contains(e.target)) this.close();
    }

    // ---- drawer

    openDrawer(opener) {
      var d = this.drawer;
      if (!d || d.open) return;
      d.classList.remove('is-closing');
      if (typeof d.showModal === 'function') d.showModal(); else d.setAttribute('open', '');
      document.documentElement.classList.add('dvf-lock');
      this.opener = opener || null;
      if (opener) opener.setAttribute('aria-expanded', 'true');
      startTicker();
    }

    closeDrawer(instant) {
      var d = this.drawer;
      if (!d || !d.open) return;
      var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (instant || reduce || d.classList.contains('is-closing')) {
        d.classList.remove('is-closing');
        if (typeof d.close === 'function') d.close(); else { d.removeAttribute('open'); this.onDrawerClose(); }
        return;
      }
      d.classList.add('is-closing');
      var self = this;
      var done = function () {
        d.removeEventListener('animationend', done);
        clearTimeout(fallback);
        if (d.classList.contains('is-closing')) self.closeDrawer(true);
      };
      var fallback = setTimeout(done, 300); // in case animationend never fires
      d.addEventListener('animationend', done);
    }

    onDrawerClick(e) {
      if (e.target === this.drawer) { this.closeDrawer(); return; } // the backdrop
      if (e.target.closest('[data-dvf-drawer-close]')) { e.preventDefault(); this.closeDrawer(); }
    }

    onDrawerCancel(e) {
      e.preventDefault(); // Escape: animate out instead of vanishing
      this.closeDrawer();
    }

    onDrawerClose() {
      this.drawer.classList.remove('is-closing');
      document.documentElement.classList.remove('dvf-lock');
      if (this.opener) {
        this.opener.setAttribute('aria-expanded', 'false');
        if (document.activeElement === document.body || !document.activeElement) this.opener.focus();
      }
    }
  }

  if (window.customElements && !window.customElements.get('dvf-header')) {
    window.customElements.define('dvf-header', DvfHeader);
  }

  // Exposed for tests and the theme editor console.
  window.DVFNav = {
    nextDailyEnd: nextDailyEnd,
    formatLeft: formatLeft,
    paintCountdowns: paintCountdowns,
    startTicker: startTicker,
    stopTicker: stopTicker,
    suggestCache: suggestCache,
    DvfSearch: DvfSearch,
    OPEN_DELAY: OPEN_DELAY,
    CLOSE_DELAY: CLOSE_DELAY
  };
})();
